import { useEffect, type RefObject } from 'react';
import { pageScrollerOf } from './useScrollSpy';

/** Tailwind's `lg`, where the side menu sits beside the content and is sticky. */
export const SIDE_MENU_WIDE_QUERY = '(min-width: 1024px)';

/**
 * Caps a sticky side menu's height at what the page's scroll region shows below its padding, so a menu
 * taller than the window scrolls on its own (spec:275). Measured, because the top bar's height is not
 * fixed from `lg` (components/shell/Topbar.tsx), and written in rem through the DOM (index.css scales
 * the root to 80% on mid-size screens; the rem gate keeps px out of the styles): never a JSX style.
 * Re-measured when the scroll region resizes. Below `lg` the menu is a row above the content and is
 * left alone.
 */
export function useStickyMaxHeight(target: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = target.current;
    const scroller = pageScrollerOf(el);
    if (!el || !scroller) return;
    const wide = typeof window.matchMedia === 'function' ? window.matchMedia(SIDE_MENU_WIDE_QUERY) : null;
    let frame: number | null = null;

    const write = () => {
      frame = null;
      if (!wide?.matches) {
        el.style.removeProperty('max-height');
        return;
      }
      const style = getComputedStyle(scroller);
      const available =
        scroller.clientHeight - (parseFloat(style.paddingTop) || 0) - (parseFloat(style.paddingBottom) || 0);
      const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      if (available <= 0) el.style.removeProperty('max-height');
      else el.style.maxHeight = `${Math.floor((available / rootSize) * 1000) / 1000}rem`;
    };
    const schedule = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(write);
    };

    write();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(scroller);
    wide?.addEventListener?.('change', schedule);
    window.addEventListener('resize', schedule);
    return () => {
      observer?.disconnect();
      wide?.removeEventListener?.('change', schedule);
      window.removeEventListener('resize', schedule);
      if (frame !== null) cancelAnimationFrame(frame);
      el.style.removeProperty('max-height');
    };
  }, [target]);
}
